/**
 * Cart Drawer Component
 * 
 * Slide-out drawer showing Shopify cart contents.
 * Allows updating quantities, removing items, and proceeding to checkout.
 */
import { useShopify } from '@/context/ShopifyContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { useCurrency } from '@/context/CurrencyContext';
import { X, ShoppingBag, Minus, Plus, Trash2, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import ImageWithFallback from '@/components/common/ImageWithFallback';

const CartDrawer = () => {
  const { 
    cart, 
    cartOpen, 
    setCartOpen, 
    loading,
    error,
    resetCart,
    removeFromCart, 
    updateQuantity, 
    goToCheckout, 
    itemCount,
    subtotal 
  } = useShopify();
  const { isShopifyEnabled } = useSiteSettings();
  const { formatPrice } = useCurrency();

  // Don't render if Shopify is disabled or cart is not open
  if (!isShopifyEnabled || !cartOpen) return null;

  return (
    <div className="fixed inset-0 z-50">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={() => setCartOpen(false)} 
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <ShoppingBag className="w-5 h-5" />
            <h2 className="font-heading text-xl">Your Bag</h2>
            {itemCount > 0 && (
              <span className="bg-black text-white text-xs px-2 py-0.5 rounded-full">
                {itemCount}
              </span>
            )}
          </div>
          <button 
            onClick={() => setCartOpen(false)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <AlertCircle className="w-12 h-12 text-red-400 mb-4" />
            <p className="text-gray-700 font-medium mb-2 text-center">Something went wrong</p>
            <p className="text-gray-500 text-sm text-center mb-6">{error}</p>
            <button
              onClick={resetCart}
              className="flex items-center gap-2 px-6 py-2 bg-black text-white text-sm font-mono uppercase tracking-wider hover:bg-gray-800 transition-colors"
            >
              <RefreshCw className="w-4 h-4" />
              Reset Cart
            </button>
          </div>
        )}
        
        {/* Empty State */}
        {!loading && !error && (!cart?.lineItems || cart.lineItems.length === 0) && (
          <div className="flex-1 flex flex-col items-center justify-center p-8">
            <ShoppingBag className="w-16 h-16 text-gray-200 mb-4" />
            <p className="text-gray-500 font-medium mb-2">Your bag is empty</p>
            <p className="text-gray-400 text-sm text-center">
              Add some items from our collection to get started
            </p>
            <button
              onClick={() => setCartOpen(false)}
              className="mt-6 px-6 py-2 border border-black text-sm font-mono uppercase tracking-wider hover:bg-black hover:text-white transition-colors"
            >
              Continue Shopping
            </button>
          </div>
        )}
        
        {/* Cart Items */}
        {!loading && !error && cart?.lineItems?.length > 0 && (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {cart.lineItems.map((item) => (
                <div key={item.id} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0">
                  {/* Product Image */}
                  <div className="w-24 h-24 bg-gray-100 rounded overflow-hidden flex-shrink-0">
                    {item.variant?.image?.src ? (
                      <ImageWithFallback 
                        src={item.variant.image.src} 
                        alt={item.title}
                        className="w-full h-full object-cover"
                        containerClassName="w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ShoppingBag className="w-8 h-8 text-gray-300" />
                      </div>
                    )}
                  </div>
                  
                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-medium text-sm leading-tight truncate">
                      {item.title}
                    </h3>
                    {item.variant?.title && item.variant.title !== 'Default Title' && (
                      <p className="text-xs text-gray-500 mt-0.5">
                        {item.variant.title}
                      </p>
                    )}
                    <p className="font-mono text-sm mt-1">
                      {formatPrice(item.variant?.price?.amount || 0, { fromCurrency: item.variant?.price?.currencyCode || 'EUR' })}
                    </p>
                    
                    {/* Quantity Controls */}
                    <div className="flex items-center gap-3 mt-3">
                      <div className="flex items-center border border-gray-200 rounded">
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          className="p-1.5 hover:bg-gray-100 transition-colors"
                          aria-label="Decrease quantity"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-3 text-sm font-mono min-w-[2rem] text-center">
                          {item.quantity}
                        </span>
                        <button 
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          className="p-1.5 hover:bg-gray-100 transition-colors"
                          aria-label="Increase quantity"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>
                      
                      <button 
                        onClick={() => removeFromCart(item.id)}
                        className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        aria-label="Remove item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 space-y-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Subtotal</span>
                <span className="font-mono text-lg font-medium">
                  {formatPrice(subtotal, { fromCurrency: 'EUR' })}
                </span>
              </div>
              
              <p className="text-xs text-gray-500 text-center">
                Shipping & taxes calculated at checkout
              </p>
              
              <button
                onClick={goToCheckout}
                className="w-full bg-black text-white py-3.5 font-mono uppercase tracking-wider text-sm hover:bg-gray-800 transition-colors"
              >
                Checkout
              </button>
              
              <button
                onClick={() => setCartOpen(false)}
                className="w-full py-2 text-sm text-gray-600 hover:text-black transition-colors"
              >
                Continue Shopping
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CartDrawer;
